import { IconAlertTriangle, IconInfoCircle, IconPhone } from "@tabler/icons-react"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { hasWrittenWebAddress } from "../lib/seasonForm"

/** The website message as the public booking page shows it while booking is closed. */
export function WebsiteMessagePreview({ message }: { message: string }) {
  const text = message.trim()

  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-xs font-medium text-muted-foreground">What the public sees</span>
      {text ? (
        <Alert>
          <IconInfoCircle />
          <AlertDescription className="whitespace-pre-line">{text}</AlertDescription>
        </Alert>
      ) : (
        <p className="text-sm text-muted-foreground italic">No message.</p>
      )}
    </div>
  )
}

interface PhoneMessagePreviewProps {
  message: string
  /** Played instead when `message` is blank — the English text, for Spanish callers. */
  fallback?: string
}

/** A phone message as the text-to-speech engine will read it, with a nudge when an address is written rather than spelled out. */
export function PhoneMessagePreview({ message, fallback }: PhoneMessagePreviewProps) {
  const text = message.trim()
  const spoken = text || fallback?.trim() || ""

  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-xs font-medium text-muted-foreground">What callers hear</span>
      {!text && fallback !== undefined && (
        <p className="text-xs text-muted-foreground">No Spanish message, so Spanish callers hear the English one:</p>
      )}
      {spoken ? (
        <div className="flex gap-2 rounded-lg border bg-muted/40 px-4 py-3 text-sm">
          <IconPhone className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
          <p className="whitespace-pre-line">{spoken}</p>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground italic">No message.</p>
      )}
      {text && hasWrittenWebAddress(text) && (
        <p className="flex items-start gap-1.5 text-xs text-amber-700 dark:text-amber-400">
          <IconAlertTriangle className="mt-px size-3.5 shrink-0" />
          This looks like a written web address. Text-to-speech reads those badly — spell it out, for example
          "P S E dot com".
        </p>
      )}
    </div>
  )
}
